import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

const RoomAvailability = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [bookingsdata, setBookingsdata] = useState([]);
  const [bookinstatus, setBookingstatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .then(() => {
          setIsFullScreen(true);
        })
        .catch((err) => {
          console.error("Error entering fullscreen:", err);
          toast.error("Failed to enter full-screen mode!", {
            position: "top-right",
          });
        });
    } else {
      document
        .exitFullscreen()
        .then(() => {
          setIsFullScreen(false);
        })
        .catch((err) => {
          console.error("Error exiting fullscreen:", err);
          toast.error("Failed to exit full-screen mode!", {
            position: "top-right",
          });
        });
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formattedTime = currentTime.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
  const getOrdinalSuffix = (day) => {
    if (day % 10 === 1 && day !== 11) return "st";
    if (day % 10 === 2 && day !== 12) return "nd";
    if (day % 10 === 3 && day !== 13) return "rd";
    return "th";
  };
  const day = currentTime.getDate();
  const formattedDate = currentTime
    .toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "Asia/Kolkata",
    })
    .replace(/\d+/, `${day}${getOrdinalSuffix(day)}`);

  const parseTimeToDate = (timeString) => {
    if (!timeString || typeof timeString !== "string") return null;
    try {
      const [time, period] = timeString.split(" ");
      if (!time || !period) return null;
      let [hours, minutes] = time.split(":").map(Number);
      if (isNaN(hours) || isNaN(minutes)) return null;
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
      const today = new Date();
      const date = new Date(today);
      date.setHours(hours, minutes, 0, 0);
      return date;
    } catch (err) {
      console.error("Error parsing time:", timeString, err);
      return null;
    }
  };

  // Helper function to check if current time is within a time slot
  const isCurrentTimeInSlot = (timeSlot) => {
    if (!timeSlot || typeof timeSlot !== "string") {
      console.warn("Invalid timeSlot:", timeSlot);
      return false;
    }
    try {
      const [startTime, endTime] = timeSlot.split(" - ");
      if (!startTime || !endTime) {
        console.warn("Invalid timeSlot format:", timeSlot);
        return false;
      }
      const now = new Date();
      const slotStart = parseTimeToDate(startTime);
      const slotEnd = parseTimeToDate(endTime);
      if (!slotStart || !slotEnd) {
        console.warn("Invalid slot times:", { startTime, endTime });
        return false;
      }
      return now >= slotStart && now < slotEnd;
    } catch (err) {
      console.error("Error in isCurrentTimeInSlot:", timeSlot, err);
      return false;
    }
  };

  const isMeetingCompleted = (timeSlot) => {
    if (!timeSlot || typeof timeSlot !== "string") return false;
    try {
      const [, endTime] = timeSlot.split(" - ");
      if (!endTime) return false;
      const now = new Date();
      const slotEnd = parseTimeToDate(endTime);
      if (!slotEnd) return false;
      return now >= slotEnd; // Meeting is completed if current time is past the end time
    } catch (err) {
      console.error("Error in isMeetingCompleted:", timeSlot, err);
      return false;
    }
  };

  const handleGetHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `https://api.more.in/v1/slotify`,
        {rawPath: "/bookings_by_cabin", cabin_id: "naboo" },
        {
          headers: {
            authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        }
      );
      if (response?.data?.status === "success") {
        const history = response?.data?.details || [];
        setBookingsdata(history);
        setBookingstatus(response?.data?.cabin_status);
        setLoading(false);
      } else {
        toast.error("View booking History Failed, Please try again later!", {
          position: "top-right",
        });
        setLoading(false);
      }
    } catch (err) {
      console.error("API Error:", err);
      toast.error("An error occurred while fetching history!", {
        position: "top-right",
      });
      setLoading(false);
    }
  };
  const ongoingEvent = bookingsdata.find((event) =>
    isCurrentTimeInSlot(event.slot)
  );
  const upcomingOrOngoingBookings = bookingsdata.filter(
    (event) => !isMeetingCompleted(event.slot)
  );

  useEffect(() => {
    handleGetHistory();
    const intervalId = setInterval(() => {
      handleGetHistory();
    }, 1 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw", // Explicitly set width to viewport width
        margin: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#000",
        color: "#fff",
        fontFamily: "Roboto, sans-serif",
        overflow: "hidden", // Prevent scrollbars
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#1a2526",
          padding: "10px 20px",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h6" component="span">
            {formattedTime}
            {"\u00A0\u00A0\u00A0"}
            {formattedDate}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: "center" }}>
          <Typography variant="h4" component="span" sx={{ fontWeight: "bold" }}>
            Naboo
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={toggleFullScreen}
          style={{ color: "#1a2526", backgroundColor: "#FFFFFF" }}
        >
          {isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
        </Button>
      </Box>
      {loading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "black", // Match the background
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "white" }} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: ongoingEvent ? "#FF5733" : "#00c853",
            }}
          >
            {ongoingEvent ? (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h2" sx={{ fontWeight: "bold" }}>
                  {ongoingEvent.slot}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ marginTop: "10px", marginLeft: "10px" }}
                >
                  {formattedDate}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ marginTop: "10px", marginLeft: "10px" }}
                >
                  {ongoingEvent.event}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ marginTop: "10px", marginLeft: "10px" }}
                >
                  {ongoingEvent.create_id}
                </Typography>
              </Box>
            ) : (
              <Typography variant="h1" sx={{ fontWeight: "bold" }}>
                Room Available
              </Typography>
            )}
          </Box>
          <Paper
            sx={{
              backgroundColor: "#1a2526",
              padding: "20px",
              borderRadius: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <EventIcon
              sx={{ color: "white", fontSize: "40px", width: 60, height: 60 }}
            />{" "}
            {/* Increased size */}
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                whiteSpace: "nowrap",
                flex: 1,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              {upcomingOrOngoingBookings.map((event, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: "150px",
                      padding: "10px",
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "white" }}>
                      {event.slot}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      {formattedDate}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      {event.event}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      {event.create_id}
                    </Typography>
                  </Box>
                  {index < upcomingOrOngoingBookings.length - 1 && (
                    <Box
                      sx={{
                        width: "4px",
                        height: "60px",
                        backgroundColor: "#fff",
                        margin: "0 5px",
                        height: "90%",
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default RoomAvailability;
